import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { ModernPickingList } from './ModernPickingList';
import { ModernPickingDetails } from './ModernPickingDetails';
import { BatchPackagesModal } from './modals/BatchPackagesModal';
import { pickingService } from '../../../../services/pickingService';
import { settingsService } from '../../../../services/settingsService';
import { logger } from '../../../../services/loggingService';
import { PListDto } from '../../../../types/picking';
import { LockConflictError } from '../../../../utils/lockConflictError';

interface ModernPickingAppProps {
  onBack?: () => void;
  initialPListId?: number;
}

export const ModernPickingApp: React.FC<ModernPickingAppProps> = ({ onBack, initialPListId }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [currentView, setCurrentView] = useState<'list' | 'picking'>(initialPListId ? 'picking' : 'list');
  const [selectedPListId, setSelectedPListId] = useState<number | null>(initialPListId ?? null);
  const [pickingLists, setPickingLists] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Batch picking modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [pendingBatchPick, setPendingBatchPick] = useState<PListDto | null>(null);

  // Operator batch picking setting
  const [allowOperatorBatchPicking, setAllowOperatorBatchPicking] = useState(false);

  // Tidspunkt for siste vellykkede last — brukes til debounce av fane-fokus-refresh
  // slik at vi ikke dobbelt-refresher rett etter navigasjon tilbake til listen.
  const lastLoadedAtRef = useRef<number>(0);
  const VISIBILITY_REFRESH_DEBOUNCE_MS = 5000;

  useEffect(() => {
    logger.info('ModernPickingApp', 'Component mounted, loading picking lists');
    loadPickingLists(true);
    loadOperatorBatchSetting();
  }, []);

  // Refresh listen når nettleserfanen får fokus igjen (fanger stale klient-state
  // der en annen operatør har tatt en plukkliste mens fanen lå i bakgrunnen).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (currentView !== 'list') return;
      if (loading) return;
      if (Date.now() - lastLoadedAtRef.current < VISIBILITY_REFRESH_DEBOUNCE_MS) return;
      loadPickingLists();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [currentView, loading]);

  const loadOperatorBatchSetting = async () => {
    try {
      // Use effective endpoint so we resolve via the standard hierarchy
      // (Profile -> System -> DefaultValue from SettingDefinition). This avoids a
      // 404 when the tenant has not explicitly set a value - DefaultValue 'false'
      // from master is returned instead.
      const setting = await settingsService.getEffectiveSetting('picking.AllowOperatorBatchPicking');
      const value = setting?.currentValue;
      setAllowOperatorBatchPicking(value === 'true' || value === '1');
    } catch {
      // Default to false if setting can't be loaded
      setAllowOperatorBatchPicking(false);
    }
  };

  const loadPickingLists = async (isInitialLoad = false): Promise<PListDto[] | undefined> => {
    const startTime = Date.now();

    try {
      // Only show full loading spinner on initial load — refreshes keep the current view
      if (isInitialLoad) setLoading(true);
      setError(null);

      logger.info('ModernPickingApp', 'Starting to load picking lists');

      // Bruker getAvailablePickingLists som håndterer lås-logikken og statusfiltrering
      const lists = await pickingService.getAvailablePickingLists(undefined, currentWarehouse || undefined);

      const duration = Date.now() - startTime;
      logger.info('ModernPickingApp', 'Successfully loaded picking lists', {
        count: lists.length,
        duration,
        availableLists: lists.filter(l => !l.lockedBy || l.lockedBy.trim() === '').length,
        lockedLists: lists.filter(l => l.lockedBy && l.lockedBy.trim() !== '').length
      });

      setPickingLists(lists);
      lastLoadedAtRef.current = Date.now();
      return lists;
    } catch (err) {
      const duration = Date.now() - startTime;
      const error = err as Error;

      // Strukturert error logging med kontekst
      logger.logException('ModernPickingApp', error, {
        operation: 'loadPickingLists',
        duration,
        userAction: 'loadData'
      });

      setError(t('warehouse.modules.picking.errors.loadPickingListsFailed'));

      // Log user-visible error
      logger.warn('ModernPickingApp', 'User shown error message for failed picking lists load', {
        errorMessage: t('warehouse.modules.picking.errors.loadPickingListsFailed'),
        originalError: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPicking = async (pickingList: PListDto) => {
    const startTime = Date.now();

    // Log user action
    logger.logUserAction('select_picking_list', {
      pListID: pickingList.pListID,
      priority: pickingList.priority,
      status: pickingList.pListStatus,
      isLocked: !!pickingList.lockedBy,
      lockedBy: pickingList.lockedBy,
      isBatchPick: pickingList.pListIsBatchYesNo
    });

    try {
      // Start picking - this will lock, set status to 20 (Under arbeid), and set start time
      logger.info('ModernPickingApp', 'Attempting to start picking', {
        pListID: pickingList.pListID,
        description1: pickingList.description1,
        priority: pickingList.priority,
        isBatchPick: pickingList.pListIsBatchYesNo,
        currentLockedBy: pickingList.lockedBy
      });

      await pickingService.startPicking(pickingList.pListID);

      logger.info('ModernPickingApp', 'Successfully started picking', {
        pListID: pickingList.pListID
      });

      // Check if this is a batch pick - show packages modal before starting
      if (pickingList.pListIsBatchYesNo) {
        logger.info('ModernPickingApp', 'Batch pick detected, showing packages modal', {
          pListID: pickingList.pListID
        });

        setPendingBatchPick(pickingList);
        setShowBatchModal(true);
        return; // Don't proceed to picking yet - wait for modal callback
      }

      // Not a batch pick - proceed directly to picking
      setSelectedPListId(pickingList.pListID);
      setCurrentView('picking');

      const duration = Date.now() - startTime;
      logger.info('ModernPickingApp', 'Successfully started picking session', {
        pListID: pickingList.pListID,
        duration,
        view: 'picking'
      });

    } catch (err) {
      const duration = Date.now() - startTime;
      const error = err as Error;

      if (err instanceof LockConflictError) {
        // Lock-konflikt er normal concurrency-kontroll (forretningsflyt), ikke en systemfeil.
        logger.warn('ModernPickingApp', 'Picking list locked by another operator', {
          operation: 'handleSelectPicking',
          pListID: pickingList.pListID,
          lockedBy: err.lockedBy,
          duration
        });
        alert(`Plukkliste #${pickingList.pListID} er allerede under arbeid av ${err.lockedBy}.\n\nVelg en annen plukkliste.`);
      } else {
        logger.logException('ModernPickingApp', error, {
          operation: 'handleSelectPicking',
          pListID: pickingList.pListID,
          duration,
          wasLocked: !!pickingList.lockedBy
        });
        alert(t('warehouse.modules.picking.errors.lockFailed'));
      }
      loadPickingLists(); // Alltid refresh etter feil
    }
  };

  // Handle batch modal continue - proceed to picking
  const handleBatchModalContinue = () => {
    if (pendingBatchPick) {
      logger.info('ModernPickingApp', 'Batch modal continue - starting picking', {
        pListID: pendingBatchPick.pListID
      });

      setShowBatchModal(false);
      setSelectedPListId(pendingBatchPick.pListID);
      setCurrentView('picking');
      setPendingBatchPick(null);
    }
  };

  // Handle batch modal cancel - unlock the pick and return to list
  const handleBatchModalCancel = async () => {
    if (pendingBatchPick) {
      logger.info('ModernPickingApp', 'Batch modal cancelled - unlocking picking list', {
        pListID: pendingBatchPick.pListID
      });

      try {
        await pickingService.unlockPickingList(pendingBatchPick.pListID);
        logger.info('ModernPickingApp', 'Successfully unlocked picking list after batch modal cancel');
      } catch (err) {
        logger.warn('ModernPickingApp', 'Failed to unlock picking list after batch modal cancel', {
          error: (err as Error).message
        });
      }

      setShowBatchModal(false);
      setPendingBatchPick(null);
      loadPickingLists(); // Refresh list to show updated lock status
    }
  };

  const handleNextPicking = async () => {
    logger.logUserAction('click_next_picking');
    
    // Finn høyeste prioritets plukkliste som er tilgjengelig
    const availableLists = pickingLists.filter(list => 
      list.pListStatus > 0 && list.pListStatus < 50 && 
      (!list.lockedBy || list.lockedBy.trim() === '')
    );
    
    logger.info('ModernPickingApp', 'Searching for next available picking list', {
      totalLists: pickingLists.length,
      availableCount: availableLists.length,
      criteria: {
        statusRange: '> 0 AND < 50',
        unlocked: true
      }
    });
    
    if (availableLists.length > 0) {
      // Sortere etter prioritet (lavest nummer = høyest prioritet), deretter plukklistenummer
      const sortedLists = [...availableLists].sort((a, b) => {
        // Først sammenligne prioritet (lavest nummer = høyest prioritet)
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        // Hvis samme prioritet, velg laveste plukklistenummer
        return a.pListID - b.pListID;
      });
      
      const highestPriority = sortedLists[0];
      
      logger.info('ModernPickingApp', 'Found highest priority picking list for next picking', {
        selectedPListID: highestPriority.pListID,
        priority: highestPriority.priority,
        description1: highestPriority.description1,
        candidateCount: availableLists.length
      });
      
      await handleSelectPicking(highestPriority);
    } else {
      logger.warn('ModernPickingApp', 'No available picking lists found for next picking', {
        totalLists: pickingLists.length,
        lockedCount: pickingLists.filter(l => l.lockedBy && l.lockedBy.trim() !== '').length,
        statusFiltered: pickingLists.filter(l => l.pListStatus <= 0 || l.pListStatus >= 50).length
      });
      
      alert('Ingen tilgjengelige plukklister funnet!');
    }
  };

  const handleBatchCreated = async (newPListId: number) => {
    logger.info('ModernPickingApp', 'Operator batch created, refreshing and navigating', { newPListId });
    const lists = await loadPickingLists();
    if (lists) {
      const newBatch = lists.find(l => l.pListID === newPListId);
      if (newBatch) {
        await handleSelectPicking(newBatch);
      }
    }
  };

  const handleBackToList = () => {
    logger.logUserAction('back_to_list', {
      fromPListID: selectedPListId,
      previousView: currentView
    });
    
    setCurrentView('list');
    setSelectedPListId(null);
    loadPickingLists();
    
    logger.info('ModernPickingApp', 'Returned to picking list view and refreshed data');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-gray-600">Laster plukklister...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => {
            logger.logUserAction('retry_load_picking_lists');
            loadPickingLists();
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  if (currentView === 'picking' && selectedPListId) {
    return (
      <ModernPickingDetails 
        pListId={selectedPListId} 
        onBack={handleBackToList} 
      />
    );
  }

  return (
    <>
      <ModernPickingList
        pickingLists={pickingLists}
        onSelectPicking={handleSelectPicking}
        onNextPicking={handleNextPicking}
        onRefreshLists={() => {
          logger.logUserAction('refresh_picking_lists');
          loadPickingLists();
        }}
        onBack={onBack}
        onBatchCreated={handleBatchCreated}
        allowOperatorBatchPicking={allowOperatorBatchPicking}
      />

      {/* Batch packages modal - shown before starting batch picks */}
      <BatchPackagesModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onContinue={handleBatchModalContinue}
        onCancel={handleBatchModalCancel}
        pListId={pendingBatchPick?.pListID || 0}
        pListDescription={pendingBatchPick?.description1}
      />
    </>
  );
};
